import { Avatar, Dialog, DialogContent, Grid, InputAdornment, Pagination, Typography } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AppTransComponents } from "app/appTrans";
import { MprcDialogTitleClose, MprcRequestIndicator } from "components";
import { MprcFilterBoxContent } from "components/filter";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { MprcFilterNoRecords } from "components/filter/MprcFilterNoRecords";
import { IconButtonCancel, IconButtonDelete } from "components/iconButtons";
import { IconAdd, IconSearch } from "components/icons";
import { AutoCompleteDataModel, AutoCompleteSuggestions, MprcAutoComplete } from "components/MprcAutoComplete";
import { MprcBarTitle } from "components/MprcBarTitle";
import { MprcConfirmDialog } from "components/MprcConfirmDialog";
import { MprcContainer } from "components/MprcContainer";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcRequestErrorDialog } from "components/MprcRequestErrorDialog";
import { MprcTextFieldColor } from "components/MprcTextFieldColor";
import { GetRoleSuggestionsPayload, GroupType, ManagedUserGroupsQueryResponse, ManagedUserGroupsQueryResponsePaginatedList, ManagedUserGroupsQueryStatus, MemberType } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { GroupMembersDrawer } from "views/foundation/group/drawers/GroupMembersDrawer";
import { EditUserHeader } from "views/foundation/user/Home/EditUser/EditUserHeader";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    userId: string;
    userName: string;
    viewMode?: "dialog" | "form";
    onClose: () => void;
}

type FormMode = "adding" | "filtering";

////////////////////////////////////////////////////////////////////////////

/**
 * Edição dos Grupos do Usuário
 * @param props
 */
export const EditUserGroups: React.FC<IProps> = (props) => {
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    ////////////////////////////////////////////////////////////////////////////
    const { userId, userName, viewMode, onClose } = props;

    const [loading, setLoading] = useState<boolean>(true);
    const [adding, setAddGroup] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [filtered, setFiltered] = useState<boolean>(false);
    const [formMode, setFormMode] = useState<FormMode>("adding");

    const [dialog, setDialog] = useState<any | null>(null);
    const [viewError, setViewError] = useState<any>();

    const [viewData, setViewData] = useState<Nullable<ManagedUserGroupsQueryResponsePaginatedList>>(null);
    const pageNumber = useRef<number>(1);
    const searchRef = useRef<HTMLInputElement>(undefined);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const refreshData = (page: number) => {
        pageNumber.current = page;
        setLoading(true);
    };

    ///
    const handleCancelSearch = () => {
        setSearchText("");
        refreshData(1);
    };

    ///
    const handleDelete = (item: ManagedUserGroupsQueryResponse) => {
        setDialog(<MprcConfirmDialog
            open
            severity="danger"
            message={
                <Trans
                    i18nKey="User.Edit.Groups.ConfirmDeleteMessage"
                    values={{ name: item.name }}
                    components={AppTransComponents}
                />
            }
            onClose={
                (result) => {
                    if (result) {
                        setDeleting(item.groupId);
                    }
                    setDialog(null);
                }
            }
        />);
    };

    ///
    const getGroupSuggestions = (searchText: string, pageNumber: number, pageSize: number): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        searchText = searchText.toLowerCase();

        const params: GetRoleSuggestionsPayload = {
            userId,
            searchText,
            pageNumber,
            pageSize
        };

        return new Promise((resolve) => {
            api.v1.userManagement.getGroupSuggestions(params)
                .then(result => {
                    resolve({
                        items: result.data.items as AutoCompleteDataModel[],
                        totalRows: result.data.totalRows
                    });
                })
                .catch(e => {
                    resolve({ items: [], totalRows: 0 });
                    setViewError(e);
                });
        });
    };

    ///
    const handleAddGroup = (items: AutoCompleteDataModel[]) => {
        if (items) setAddGroup(items[0].key);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const RenderFilter = (
        <MprcContainer margin="dense">
            <MprcCardFilterBox>
                <MprcFilterBoxContent
                    title={t("User.Edit.Groups.Title")}
                    filterAction={
                        {
                            readonly: false,
                            filtered: formMode == "filtering",
                            onClick: (e) => setFormMode(formMode == "filtering" ? "adding" : "filtering")
                        }
                    }
                >
                    <Grid container justifyContent={"space-between"}>
                        {
                            formMode == "filtering"
                                ? (
                                    <Grid size="grow" sx={(theme) => ({ padding: theme.spacing(1) })}>
                                        <MprcTextFieldColor
                                            autoFocus
                                            fullWidth
                                            placeholder={t("Search.TextPlaceholder", { ns: "shared" })}
                                            inputColor="warning"
                                            underlineColor="warning"
                                            value={searchText}
                                            inputRef={(ref) => searchRef.current = ref}
                                            onChange={(e) => setSearchText(e.currentTarget.value)}
                                            onKeyDown={(e) => { if (e.keyCode == 13) refreshData(1); }}
                                            endAdornment={
                                                searchText == ""
                                                    ? null
                                                    : <IconButtonCancel color="warning" size="small" onClick={handleCancelSearch} />
                                            }
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <IconSearch fontSize="inherit" />
                                                </InputAdornment>
                                            }
                                        />
                                    </Grid>
                                )
                                : (
                                    <Grid size="grow">
                                        <Grid container>
                                            <Grid size="grow">
                                                <MprcAutoComplete
                                                    id="groupsInput"
                                                    placeholder={t("User.Edit.Groups.AddGroupPlaceholder")}
                                                    autoFocus
                                                    selectedItems={[]}
                                                    onGetSuggestions={getGroupSuggestions}
                                                    onChange={handleAddGroup}
                                                    multiSelect={false}
                                                    startAdornment={
                                                        <InputAdornment position="start" key="AutoCompleteAdorment">
                                                            <IconAdd fontSize="inherit" />
                                                        </InputAdornment>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )
                        }
                    </Grid>
                </MprcFilterBoxContent>
            </MprcCardFilterBox>
        </MprcContainer>
    );

    ///
    const RenderGrid = (
        <Grid
            container
            justifyContent="center"
            alignItems="center"
            spacing={2}
        >
            {
                viewData?.items.map((item, index) => {

                    const disabled = item.status != ManagedUserGroupsQueryStatus.Active || !item.directGroup;

                    return (
                        <Grid key={index} size={{ xs: 12 }} width="100%">
                            <Grid
                                container
                                justifyContent="center"
                                alignItems="center"
                                spacing={2}
                                wrap="nowrap"
                                sx={{ pb: 1 }}
                                className={AppGlobalClassKeys.lineHighlight}
                            >
                                <Grid>
                                    <Avatar
                                        variant="circular"
                                        sx={
                                            {
                                                width: 32,
                                                height: 32,
                                                backgroundColor: "#f5f5f5"
                                            }
                                        }
                                        src={item.thumbnailUrl}
                                    />
                                </Grid>

                                <Grid size="grow" overflow="hidden">
                                    <Grid container direction="column" spacing={0.5} flexWrap="nowrap" width="100%">
                                        <Grid overflow="hidden">
                                            <MprcListItemTitle
                                                disabled={disabled}
                                                onClick={() => {
                                                    setDialog(<GroupMembersDrawer
                                                        groupId={item.groupId}
                                                        groupName={item.name}
                                                        onClose={() => setDialog(null)}
                                                    />);
                                                }}
                                            >
                                                {item.name}
                                            </MprcListItemTitle>
                                        </Grid>

                                        {
                                            (item.status != ManagedUserGroupsQueryStatus.Active || !item.directGroup) &&
                                            <Grid size="grow">
                                                <Typography
                                                    variant="caption"
                                                    color={"muted.main"}
                                                >
                                                    {item.status == ManagedUserGroupsQueryStatus.Adding && <span>{t("User.Edit.Groups.ProcessingAddText")}</span>}
                                                    {item.status == ManagedUserGroupsQueryStatus.Deleting && <span>{t("User.Edit.Groups.ProcessingDeleteText")}</span>}
                                                    {!item.directGroup && <span>{t("User.Edit.Groups.IndirectGroupInfoText")}</span>}
                                                </Typography>
                                            </Grid>
                                        }
                                    </Grid>

                                </Grid>

                                <Grid>
                                    {
                                        item.groupType == GroupType.Static && item.status != ManagedUserGroupsQueryStatus.Deleting && item.directGroup &&
                                        <IconButtonDelete size="small" onClick={() => handleDelete(item)} />
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    );
                })
            }
        </Grid>
    );

    const RenderContent = (
        <>
            {dialog}

            <MprcRequestIndicator
                visible={adding != null || deleting != null || loading}
                type={loading ? "request" : "post"} />

            <MprcRequestErrorDialog
                error={viewError}
                onClose={() => { setViewError(undefined); }}
            />

            <Grid container direction="column" flexWrap="nowrap" height="100%" spacing={0} className={AppGlobalClassKeys.containerRounded}>
                <Grid>
                    {RenderFilter}
                </Grid>
                <Grid size="grow" height="100%" overflow="auto" padding={1}>
                    {
                        viewData?.items.length == 0
                            ? <MprcFilterNoRecords
                                filtered={filtered}
                                message={t("Group.GroupSelectorDrawer.NoRecordsFound")}
                                emptyDatasetMessage={t("Group.GroupSelectorDrawer.ListEmptyMessage")}
                                onResetFilter={handleCancelSearch}
                            />
                            : RenderGrid
                    }
                </Grid>
                {
                    viewData && viewData.totalPages > 1 && (
                        <Grid alignSelf="center" padding={1}>
                            <Pagination
                                shape="rounded"
                                color="primary"
                                page={viewData.pageNumber}
                                count={viewData.totalPages}
                                onChange={(e, pageNumber) => {
                                    if (pageNumber != viewData.pageNumber) {
                                        refreshData(pageNumber);
                                    }
                                }}
                            />
                        </Grid>
                    )
                }
            </Grid>
        </>
    );

    ////////////////////////////////////////////////////////////////////////////////
    /// Effect's
    ////////////////////////////////////////////////////////////////////////////////

    // Ao carregar...
    useEffect(() => {
        ///
        async function getData() {
            try {
                var result = await api.v1.userManagement.getGroups({
                    userId: userId,
                    searchText: searchText,
                    pageNumber: pageNumber.current,
                    pageSize: APP_PAGE_SIZE_DEFAULT
                });
                setViewData(result.data);
                setFiltered(searchText != "");
                searchRef.current?.focus();
            }
            catch (e: any) {
                setViewData(null);
                setViewError(e as Error);
            }
            setLoading(false);
        }

        if (loading) getData();
    }, [loading]);


    // Ao adicionar...
    useEffect(() => {
        async function postData() {
            if (adding) {
                try {
                    await api.v1.groupManagement.addMemberStaticGroup({ groupId: adding, members: [{ memberId: userId, memberType: MemberType.User, }] });
                    setLoading(true);
                }
                catch (e) {
                    setViewError(e);
                }

                setAddGroup(null);
            }
        }

        postData();

    }, [adding]);

    // Ao remover...
    useEffect(() => {
        async function postData() {

            if (deleting) {
                try {
                    if (await api.v1.groupManagement.deleteMemberStaticGroup({ memberId: userId, memberType: MemberType.User, groupId: deleting })) {
                        enqueueSnackbar(t("User.Edit.Groups.DeleteGroupSuccessMessage"), { variant: "success" });
                        if (viewData!.items.length == 1 && pageNumber.current > 1) pageNumber.current--;
                        setLoading(true);
                    }
                    else
                        enqueueSnackbar(t("User.Edit.Groups.DeleteGroupFailMessage"), { variant: "error" });

                }
                catch (e) {
                    setViewError(e);
                }
            }

            setDeleting(null);
        }

        postData();

    }, [deleting]);


    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////


    return viewMode == "dialog"
        ? (
            <Dialog
                open
                fullWidth
                maxWidth="lg"
            >
                <MprcDialogTitleClose
                    title={userName}
                    onClose={onClose}
                />
                <DialogContent sx={{ height: "100vh" }} >
                    {RenderContent}
                </DialogContent>
            </Dialog>
        )
        : (
            <Grid container direction="column" flexWrap="nowrap" spacing={1} height="100%">
                <Grid>
                    <EditUserHeader
                        onClose={onClose}
                        recordChanged={false}
                        reloadOnClose={false}
                        actions={[<MprcBarTitle>{userName}</MprcBarTitle>]}
                    />
                </Grid>

                <Grid size="grow" height="100%">
                    {RenderContent}
                </Grid>
            </Grid>
        );
};
